/**
 * File System Access API helpers for opening the source PDF and saving
 * the signed copy beside it (same folder when the browser allows).
 */

export type SourceFileHandle = FileSystemFileHandle;

type OpenPickerWindow = Window & {
  showOpenFilePicker?: (options?: {
    types?: Array<{ description: string; accept: Record<string, string[]> }>;
    multiple?: boolean;
  }) => Promise<FileSystemFileHandle[]>;
};

export function supportsNativeFilePicker(): boolean {
  return typeof (window as OpenPickerWindow).showOpenFilePicker === 'function';
}

/** Open a PDF via the native file picker (returns handle + File for upload). */
export async function openPdfWithHandle(): Promise<{ file: File; handle: SourceFileHandle } | null> {
  const win = window as OpenPickerWindow;
  if (!win.showOpenFilePicker) return null;

  const handles = await win.showOpenFilePicker({
    multiple: false,
    types: [
      {
        description: 'PDF documents',
        accept: { 'application/pdf': ['.pdf'] },
      },
    ],
  });

  const handle = handles[0];
  if (!handle) return null;

  const file = await handle.getFile();
  if (!file.name.toLowerCase().endsWith('.pdf')) {
    throw new Error('Please choose a .pdf file');
  }
  return { file, handle };
}

/**
 * Handle to anchor the Save dialog near the source PDF.
 * FileSystemFileHandle.getParent() never shipped in any browser, but the spec's
 * showSaveFilePicker({ startIn }) accepts a *file* handle and opens its parent
 * directory — same UX without the unsupported API.
 */
export async function getSourceDirectoryHandle(
  sourceHandle: SourceFileHandle | null | undefined
): Promise<FileSystemHandle | undefined> {
  return sourceHandle ?? undefined;
}